'use client'

import { HOUSE_LIST_SHOW_MAP_KEY } from '@config'
import { NEW_HOMES_ALIAS } from '@homeSearch/modules/filter/config/content'
import SearchPopup from '@homeSearch/modules/searchPopup'
import useProgressRouter from '@hooks/useProgressRouter'
import { newHomesDataLayer } from '@plugin/google/gtm/modules/home'
import { RecentViewManager } from '@plugin/recentView'
import getHouseRoute from '@plugin/searchRoute/modules/house'
import { cacheRecentActivity } from '@plugin/user'
import { builderList, houseList } from '@router'
import { first, get } from 'lodash-es'
import { Suspense, useCallback, useEffect, useState } from 'react'
import { useLocalStorage } from 'react-use'
import useLayoutContext from '../layout/context'
import { SearchWrap } from '../modules/base'
import useHomeSearchContext, { cacheSuggestedSuburbs } from '../modules/context'
import { fetchNewHomes } from '../modules/fetch'
import HomeSearchFilter from '../modules/filter'
import { NEW_HOMES_FILTER } from '../modules/filter/config'
import { HomeSearchInputNew } from '../modules/input'
import Location from '../modules/location'
import HomeSearchPopup from '../modules/popup'

export default function NewHomesContent({
  title = 'Discover new homes',
  inputId = ''
}) {

  /**
   * 这里在input和filter外面的单独处理了 suburbs和surrounding
   * input在点击search按钮的时候，同步suburbs和surrounding到filter
   * filter在点击关闭的时候，同步数据到input
   */

  const { suburbs, surrounding, displayFilter, showFilter, hideFilter, changeSuburbs, toggleSurrounding } = useLayoutContext()

  const { pushRouter } = useProgressRouter()

  // input search同步数据到filter
  const filterHandle = useCallback(({ suburbs, surrounding }) => {
    changeSuburbs(suburbs)
    toggleSurrounding(surrounding)
    showFilter()
  }, [showFilter, changeSuburbs, toggleSurrounding])

  // 上次是否打开了地图
  const [showMapLocal] = useLocalStorage(HOUSE_LIST_SHOW_MAP_KEY, false)
  const confrimHandle = useCallback(({
    suburbs,
    surrounding,
    searchType,
    routerQuery,
    priceFrom,
    priceTo,
    bedroom,
    bathroom,
    carspaces,
    propertyType
  }) => {

    const houseRoute = getHouseRoute()
    houseRoute.setSuburbs(suburbs)
    houseRoute.surround = surrounding
    houseRoute['price-from'] = priceFrom
    houseRoute['price-to'] = priceTo
    houseRoute.bedroom = bedroom
    houseRoute.bathroom = bathroom
    houseRoute.carspaces = carspaces
    houseRoute['property-type'] = propertyType
    houseRoute.pushNewZealand()
    houseRoute.changeMapVisible(showMapLocal)
    const st = searchType || first(NEW_HOMES_FILTER.searchTypes)
    const isNewHomes = [NEW_HOMES_ALIAS.apartments, NEW_HOMES_ALIAS.land].includes(st.alias)
    if (isNewHomes) {
      houseRoute.isLandHome = st.alias === NEW_HOMES_ALIAS.land ? 1 : 0
    }
    const slug = houseRoute.toSlug()
    const route = isNewHomes
      ? houseList(get(st, 'route'), slug, { filter: routerQuery })
      : builderList(slug)

    RecentViewManager.cacheNewHomesBySuburb({
      suburbs,
      surrounding,
      priceFrom,
      priceTo,
      bedrooms: bedroom,
      bathrooms: bathroom,
      carspaces,
      propertyType,
      filter: routerQuery,
      isLandHome: houseRoute.isLandHome
    })

    cacheRecentActivity({ searchType: st, route, suburbs, slug, filter: routerQuery })
    newHomesDataLayer.searchClick(st)
    pushRouter(route)
    cacheSuggestedSuburbs(suburbs)
  }, [pushRouter, showMapLocal])

  return (
    <>
      <SearchWrap title={title} sub='Find New Build Homes, New Apartments and Terrace Housing, House and Land packages, and Home Builders across New Zealand.'>
        <NewHomesSearchContent
          onSearch={confrimHandle}
          onFilter={filterHandle}
          suburbs={suburbs}
          surrounding={surrounding}
          inputId={inputId}
        />
      </SearchWrap>
      <HomeSearchFilter
        show={displayFilter}
        suburbs={suburbs}
        surrounding={surrounding}
        setSuburbs={changeSuburbs}
        toggleSurrounding={toggleSurrounding}
        onClose={hideFilter}
        onConfirm={confrimHandle}
        filter={NEW_HOMES_FILTER}
        fetcher={fetchNewHomes}
      ></HomeSearchFilter>
    </>
  )
}


export function NewHomesSearchContent({
  onSearch,
  onFilter,
  suburbs,
  surrounding,
  inputId = ''
}) {

  const { setSuburbs, toggleSurrounding } = useHomeSearchContext()
  useEffect(() => setSuburbs(suburbs), [suburbs, setSuburbs])
  useEffect(() => toggleSurrounding(surrounding), [surrounding, toggleSurrounding])

  const [showInputPop, setShowInputPop] = useState(false)
  return (
    <Suspense>
      {
        !showInputPop && (
          <HomeSearchInputNew
            filterButton
            onSearch={onSearch}
            onFilter={onFilter}
            mobileDisableInput
            onInputClick={() => setShowInputPop(true)}
            id={inputId}
          >
            <HomeSearchPopup fetcher={fetchNewHomes}></HomeSearchPopup>
          </HomeSearchInputNew>
        )
      }
      <Location></Location>
      <SearchPopup
        show={showInputPop}
        onClose={() => setShowInputPop(false)}
        onFilter={onFilter}
        fetcher={fetchNewHomes}
        onSearch={onSearch}
      />
    </Suspense>
  )
}